<template>
    <v-container class="vpd-sub-content" fluid>
        <v-overlay :value="overlay" z-index="1000">
            <v-progress-circular indeterminate size="64" />
        </v-overlay>

        <!-- Upload card -->
        <v-card class="vpd-statement-upload pa-6 mb-6" flat>
            <div class="text-h6 mb-4">Bankszámlakivonat feltöltése</div>

            <div class="vpd-dropzone" :class="{ 'vpd-dropzone--active': dragActive }" @click="$refs.fileInput.click()"
                @dragover.prevent="dragActive = true" @dragenter.prevent="dragActive = true"
                @dragleave.prevent="dragActive = false" @drop.prevent="onDrop">
                <input ref="fileInput" type="file" accept="application/pdf" multiple class="vpd-dropzone__input"
                    @change="onFileChange" />

                <v-icon size="48" class="mb-2">mdi-cloud-upload-outline</v-icon>

                <div v-if="!selectedFiles.length" class="subtitle-1 text-center">
                    Húzza ide a PDF dokumentumot,<br />vagy kattintson a tallózáshoz
                </div>

                <div v-else class="subtitle-1">
                    <div v-for="(file, index) in selectedFiles" :key="file.name + '-' + file.lastModified"
                        class="d-flex align-center">
                        <v-icon class="mr-2">mdi-file-pdf-box</v-icon>
                        <span>{{ file.name }} ({{ formatSize(file.size) }})</span>
                        <v-btn icon small class="ml-2" @click.stop="removeFile(index)">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </div>
            </div>

            <div class="d-flex justify-center align-center">
                <v-btn class="primary huge px-12 my-6" :disabled="!selectedFiles.length || uploading" :loading="uploading"
                    @click="upload">
                    Feltöltés
                </v-btn>
            </div>
        </v-card>

        <v-divider />

        <!-- Uploaded documents list -->
        <v-card class="vpd-statement-list pa-6 mt-6" flat>
            <div class="d-flex justify-space-between align-center mb-4">
                <div class="text-h6">Feltöltött dokumentumok</div>
                <a class="vpd-account-summary__refresh_button" @click="refreshDocuments">
                    Adatok frissítése
                </a>
            </div>

            <v-data-table :headers="headers" :items="documents" :loading="loadingDocuments"
                :items-per-page="itemsPerPage" hide-default-footer disable-sort
                no-data-text="Még nincs feltöltött dokumentum" loading-text="Betöltés...">
                <template v-slot:[`item.status`]="{ item }">
                    <v-chip small :color="statusColor(item.status)" :text-color="statusTextColor(item.status)">
                        {{ statusLabel(item.status) }}
                    </v-chip>
                </template>

                <template v-slot:[`item.createdAt`]="{ item }">
                    {{ formatDate(item.createdAt) }}
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <v-btn icon small :loading="downloadingDocumentId === item.documentId"
                        :disabled="!item.documentId || downloadingDocumentId !== null" title="PDF letöltése"
                        aria-label="PDF letöltése" @click="downloadDocument(item)">
                        <v-icon>mdi-download</v-icon>
                    </v-btn>
                </template>
            </v-data-table>

            <div class="text-center pt-2" v-if="documentsLength > 1">
                <v-pagination v-model="page" :length="documentsLength" @input="onPageChange"></v-pagination>
            </div>
        </v-card>
    </v-container>
</template>

<script>
import statementReaderService from "@/services/statements-service";

export default {
    name: "statement-reader",
    props: ["config"],
    data() {
        return {
            overlay: false,
            uploading: false,
            downloadingDocumentId: null,
            loadingDocuments: false,
            dragActive: false,
            selectedFiles: [],
            documents: [],
            documentsTotalCount: 0,
            page: 1,
            itemsPerPage: 25,
            headers: [
                { text: "Fájlnév", value: "fileName" },
                { text: "Állapot", value: "status" },
                { text: "Feltöltve", value: "createdAt" },
                { text: "Letöltés", value: "actions", align: "center" },
            ],
        };
    },
    computed: {
        documentsLength: function () {
            return Math.ceil(this.documentsTotalCount / this.itemsPerPage);
        },
    },
    methods: {
        onFileChange: function (e) {
            this.setSelectedFiles(e.target.files);
            // reset so selecting the same file again still fires @change
            e.target.value = "";
        },
        onDrop: function (e) {
            this.dragActive = false;
            this.setSelectedFiles(e.dataTransfer.files);
        },
        setSelectedFiles: function (files) {
            const selectedFiles = Array.from(files || []);
            if (!selectedFiles.length) return;

            if (selectedFiles.some((file) => file.type !== "application/pdf")) {
                this.$emit("error", "Csak PDF dokumentum tölthető fel.");
                return;
            }

            this.selectedFiles.push(...selectedFiles);
        },
        removeFile: function (index) {
            this.selectedFiles.splice(index, 1);
        },
        clearSelection: function () {
            this.selectedFiles = [];
        },
        fileToBase64: function (file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                // strip the "data:application/pdf;base64," prefix
                reader.onload = () => resolve(reader.result.split(",")[1]);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },
        upload: async function () {
            if (!this.selectedFiles.length) return;
            this.uploading = true;
            try {
                const files = await Promise.all(
                    this.selectedFiles.map(async (file) => ({
                        fileName: file.name,
                        pdfBase64: await this.fileToBase64(file),
                    }))
                );

                await statementReaderService.uploadPdf(files);

                this.clearSelection();
                this.page = 1;
                await this.refreshDocuments();
            } catch (e) {
                this.$emit("error", "A feltöltés sikertelen.");
            } finally {
                this.uploading = false;
            }
        },
        onPageChange: function (page) {
            this.page = page;
            this.refreshDocuments();
        },
        downloadDocument: async function (document) {
            if (!document || !document.documentId) return;

            this.downloadingDocumentId = document.documentId;
            try {
                const data = await statementReaderService.fetchDownloadUrl(
                    document.documentId
                );

                if (!data || !data.downloadUrl) {
                    throw new Error("Missing document download URL");
                }

                const link = window.document.createElement("a");
                link.href = data.downloadUrl;
                window.document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (e) {
                this.$emit("error", "A dokumentum letöltése sikertelen.");
            } finally {
                this.downloadingDocumentId = null;
            }
        },
        refreshDocuments: async function () {
            this.loadingDocuments = true;
            try {
                const skip = (this.page - 1) * this.itemsPerPage;
                const data = await statementReaderService.fetchDocuments(
                    skip,
                    this.itemsPerPage
                );
                this.documents = (data && data.items) || [];
                this.documentsTotalCount = (data && data.totalCount) || 0;
            } finally {
                this.loadingDocuments = false;
            }
        },
        statusLabel: function (status) {
            const map = {
                uploading: "Feldolgozás alatt",
                completed: "Kész",
                review_needed: "Ellenőrzés szükséges",
                error: "Hiba",
            };
            return map[(status || "").toLowerCase()] || status || "-";
        },
        statusColor: function (status) {
            const map = {
                uploading: "grey-light",
                completed: "green",
                review_needed: "yellow",
                error: "red-google",
            };
            return map[(status || "").toLowerCase()] || "grey-light";
        },
        statusTextColor: function (status) {
            // light backgrounds get dark text, dark backgrounds get white text
            const lightBackground = ["uploading", "review_needed"];
            return lightBackground.includes((status || "").toLowerCase())
                ? "grey-dark"
                : "white";
        },
        formatDate: function (value) {
            if (!value) return "-";
            const d = new Date(value);
            if (isNaN(d.getTime())) return String(value).substring(0, 16);
            return d.toLocaleString("hu-HU", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        formatSize: function (bytes) {
            if (!bytes && bytes !== 0) return "";
            if (bytes < 1024) return bytes + " B";
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
            return (bytes / (1024 * 1024)).toFixed(1) + " MB";
        },
    },
    async created() {
        this.overlay = true;
        await this.refreshDocuments();
        this.overlay = false;
    },
};
</script>

<style scoped>
.vpd-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 160px;
    padding: 24px;
    border: 2px dashed rgba(0, 0, 0, 0.24);
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;
}

.vpd-dropzone:hover,
.vpd-dropzone--active {
    border-color: var(--v-primary-base, #1976d2);
}

.vpd-dropzone__input {
    display: none;
}
</style>
